import React, { useState, useEffect } from 'react';
import Layout from '../../layout/Layout';
import TarifGrid from '../TarifGrid';
import TarifOption from '../TarifOption';
import { calculateDays } from '../ContratFunctions';
import { PlusIcon, MinusIcon } from '@heroicons/react/16/solid';
import { FaMoneyBillWave } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { PrivateAxios } from '../../../request/axios';
import { sanitizeInput } from '../ContratFunctions';

const EstimerContrat = () => {
    const [days, setDays] = useState(0);
    const [dateDepart, setDateDepart] = useState('');
    const [dateRetour, setDateRetour] = useState('');
    const [nomDevis, setNomDevis] = useState('');
    const [destination, setDestination] = useState('');
    const [lessThan12, setLessThan12] = useState(0);
    const [lessThan18, setLessThan18] = useState(0);
    const [adults, setAdults] = useState(0);
    const [senior, setSenior] = useState(0);
    const [isOptionActive, setIsOptionActive] = useState(false);
    const [isFamily, setIsFamily] = useState(false);
    const [resultHT, setResultHT] = useState(0);
    const [resultTTC, setResultTTC] = useState(0);
    const [formError, setFormError] = useState('');
    const tarifGrid = TarifGrid;
    const optionPrice = TarifOption;
    const today = new Date().toISOString().split('T')[0];

    const showErrorFormulaire = (message) => {
        Swal.fire({
            title: "Erreur formulaire !",
            text: message,
            icon: "error"
        });
    };

    const updateCount = (count, setCount, value) => {
        setCount(Math.max(0, count + value));
    };

    const validateForm = () => {

        const today = new Date().toISOString().split('T')[0];
        const sanitizeDevis = sanitizeInput(nomDevis);
        const sanitizeDestination = sanitizeInput(destination)
        setNomDevis(sanitizeDevis);
        setDestination(sanitizeDestination);
        const departDate = new Date(dateDepart);
        const maxReturnDate = new Date(departDate);
        maxReturnDate.setDate(departDate.getDate() + 180);

        if (dateDepart < today) {
            setFormError('Merci de renseigner une date de départ valide.');
            return false;
        }

        if (dateRetour <= dateDepart) {
            setFormError('Merci de renseigner une date de retour valide.');
            return false;
        }


        if (new Date(dateRetour) > maxReturnDate) {
            setFormError('La date de retour ne peut pas dépasser 180 jours après la date de départ.');
            return false;
        }

        if (!sanitizeDestination) {
            setFormError('Merci de fournir une destination');
            return false;
        }

        if (!sanitizeDevis) {
            setFormError('Merci de fournir un nom au devis');
            return false;
        }

        if (lessThan12 <= 0 && lessThan18 <= 0 && adults <= 0 && senior <= 0) {
            setFormError('Merci d\'ajouter des voyageurs');
            return false;
        }

        setFormError(''); // Aucune erreur
        return true;
    };

    const calculateTarifFinal = () => {
        let baseTarif = tarifGrid.find(t => days >= t.minDays && days <= t.maxDays)?.price || 0;
        if (baseTarif === 0) {
            setResultHT("Aucun tarif disponible pour cette période.");
            return;
        }

        let PrimeNet = 0;
        let MtForfaitNet = 0;
        const NbA = adults || 0;
        const NbP70 = senior || 0;
        const NbP12 = lessThan18 || 0;
        const NbM12 = lessThan12 || 0;

        if (isFamily) {
            if (NbA !== 0 || NbP12 !== 0 || NbP70 !== 0) {
                if (NbM12 >= 2) {
                    PrimeNet = (baseTarif * (NbA + NbP70)) + (baseTarif * NbP12) + ((baseTarif / 2) * 2);
                    MtForfaitNet = (optionPrice * NbA) + (optionPrice * NbP12) + ((optionPrice / 2) * 2);
                } else {
                    PrimeNet = (baseTarif * (NbA + NbP70)) + (baseTarif * NbP12) + ((baseTarif / 2) * NbM12);
                    MtForfaitNet = (optionPrice * NbA) + (optionPrice * NbP12) + ((optionPrice / 2) * NbM12);
                }
            } else {
                if (NbM12 >= 5) {
                    PrimeNet = ((baseTarif / 2) * 5);
                    MtForfaitNet = ((optionPrice / 2) * 5);
                } else {
                    PrimeNet = ((baseTarif / 2) * NbM12);
                    MtForfaitNet = ((optionPrice / 2) * NbM12);
                }
            }
        } else {
            PrimeNet = (baseTarif * (NbA + NbP70)) + (baseTarif * NbP12) + (baseTarif * NbM12);
            MtForfaitNet = (optionPrice * NbA) + (optionPrice * NbP12) + (optionPrice * NbM12);
        }

        if (!isFamily) {
            const totalPeople = NbA + NbP12 + NbP70 + NbM12;
            let discount = 0;

            if (totalPeople >= 50) discount = 0.5;
            else if (totalPeople >= 40) discount = 0.4;
            else if (totalPeople >= 30) discount = 0.3;
            else if (totalPeople >= 20) discount = 0.2;
            else if (totalPeople >= 10) discount = 0.1;

            PrimeNet = PrimeNet - (PrimeNet * discount);
        }

        let totalTarif = PrimeNet + (isOptionActive ? MtForfaitNet : 0);

        setResultHT(Math.round(totalTarif).toLocaleString('fr-FR', { minimumFractionDigits: 0 }));
        setResultTTC(Math.round((totalTarif * 1.07)).toLocaleString('fr-FR', { minimumFractionDigits: 0 }));
    };

    // Utilisation de useEffect pour valider les dates à chaque changement et calculer le tarif final
    useEffect(() => {
        if (validateForm()) {
            const calculatedDays = calculateDays(dateDepart, dateRetour);
            setDays(calculatedDays);
        } else {
            setDays(0); // Réinitialiser les jours en cas d'erreur
        }
    }, [dateDepart, dateRetour, destination, nomDevis, lessThan12, lessThan18, adults, senior]);  // Déclenche l'effet à chaque changement pertinent

    useEffect(() => {
        if (days > 0) {
            calculateTarifFinal();
        } else {
            setResultHT(null);
            setResultTTC(null);
        }
    }, [days, destination, lessThan12, lessThan18, adults, senior, isOptionActive, isFamily]);

    const handleDateChange = (e, field) => {
        let value = e.target.value;

        // Vérifier si l'année contient plus de 4 chiffres
        const [year, month, day] = value.split('-');

        if (year.length > 4) {
            // Couper l'année à 4 chiffres
            value = `${year.slice(0, 4)}-${month}-${day}`;
        }

        // Mettre à jour la date correspondante
        if (field === 'depart') {
            setDateDepart(value);
        } else if (field === 'retour') {
            setDateRetour(value);
        }
    };


    const downloadDevis = async () => {
        if (nomDevis === '') {
            showErrorFormulaire('Merci de fournir un nom de destinataire pour le devis.')
            return;
        }
        if (!validateForm()) {
            return;
        }

        const result = await Swal.fire({
            title: "Téléchargement du devis",
            text: "Ne téléchargez le devis qu'à la demande du client. Vous pouvez obtenir une tarification en cliquant sur 'obtenir une estimation tarifaire' !",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Oui, télécharger !",
            cancelButtonText: 'Annuler',
            showClass: {
                popup: 'animate__animated animate__headShake'
            },
            hideClass: {
                popup: 'animate__animated animate__fadeOutUp'
            }
        });

        if (result.isConfirmed) {
            const payload = {
                dateDepart,
                dateRetour,
                destination,
                personnes: {
                    lessThan12,
                    lessThan18,
                    adults,
                    seniors: senior
                },
                optionFamille: isFamily,
                optionForfait: isOptionActive,
                nomDevis
            };

            try {
                const response = await PrivateAxios.post('/devis/download', payload, {
                    responseType: 'blob',
                    timeout: 0
                });

                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `devis_${nomDevis}.pdf`);
                link.style.display = 'none';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);

            } catch (error) {
                Swal.fire({
                    title: "Erreur de téléchargement !",
                    text: "Merci de contacter votre administrateur",
                    icon: "error"
                });
            }
        }
    };


    return (
        <Layout>
            <div className="w-2/3 p-6 mx-auto rounded-lg shadow-md bg-gray-50">
                <h1 className="mb-4 text-2xl font-bold text-axa-blue-400">Etablir un devis</h1>
                {/* Afficher une erreur si elle existe */}
                {formError && (
                    <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-lg">
                        {formError}
                    </div>
                )}
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                    <div className="">
                        <label className="block mb-2 font-semibold text-gray-700">Date de départ* : </label>
                        <input
                            required={true}
                            id="dateDepart"
                            type="date"
                            name="dateDepart"
                            value={dateDepart}
                            onChange={(e) => handleDateChange(e, 'depart')}
                            min={today}  // Limite la date minimale à aujourd'hui
                            max="9999-12-31"  // Limite les dates à une année réaliste
                            className="w-full p-2 border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-axa-blue-400"
                        />
                    </div>
                    <div className="">
                        <label className="block mb-2 font-semibold text-gray-700">Date retour* : </label>
                        <input
                            required={true}
                            id="dateRetour"
                            type="date"
                            name="dateRetour"
                            value={dateRetour}
                            onChange={(e) => handleDateChange(e, 'retour')}
                            min={dateDepart}  // Assure que la date de retour soit après la date de départ
                            max="9999-12-31"  // Limite les dates à une année réaliste
                            className="w-full p-2 border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-axa-blue-400"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2 font-semibold text-gray-700">Destination* :</label>
                        <input
                            type="text"
                            value={destination}
                            onChange={(e) => setDestination(e.target.value)}
                            required={true}
                            placeholder='Australie'
                            className="w-full p-2 border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-axa-blue-400"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-2 font-semibold text-gray-700">Devis adressé à :</label>
                        <input
                            type="text"
                            value={nomDevis}
                            onChange={(e) => setNomDevis(e.target.value)}
                            required={true}
                            placeholder='Au nom de ...'
                            className="w-full p-2 border border-gray-300 rounded-lg shadow-md focus:outline-none focus:ring-2 focus:ring-axa-blue-400"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Enfants -12 ans */}
                    <div className="flex items-center justify-between p-4 mb-4 bg-gray-100 border rounded-lg shadow-md">
                        <span className="font-bold text-gray-700">-12 ans</span>
                        <div className="flex items-center space-x-2">
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-danger-red hover:scale-110"
                                onClick={() => updateCount(lessThan12, setLessThan12, -1)}
                            >
                                <MinusIcon className="w-3 h-3" />
                            </button>
                            <span className="font-bold">{lessThan12}</span>
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-success-green hover:scale-110"
                                onClick={() => updateCount(lessThan12, setLessThan12, 1)}
                            >
                                <PlusIcon className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    {/* Enfants -18 ans */}
                    <div className="flex items-center justify-between p-4 mb-4 bg-gray-100 border rounded-lg shadow-md">
                        <span className="font-bold text-gray-700">-18 ans</span>
                        <div className="flex items-center space-x-2">
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-danger-red hover:scale-110"
                                onClick={() => updateCount(lessThan18, setLessThan18, -1)}
                            >
                                <MinusIcon className="w-3 h-3" />
                            </button>
                            <span className="font-bold">{lessThan18}</span>
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-success-green hover:scale-110"
                                onClick={() => updateCount(lessThan18, setLessThan18, 1)}
                            >
                                <PlusIcon className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    {/* Adultes */}
                    <div className="flex items-center justify-between p-4 mb-4 bg-gray-100 border rounded-lg shadow-md">
                        <span className="font-bold text-gray-700">Adultes (18 à 69 ans inclus)</span>
                        <div className="flex items-center space-x-2">
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-danger-red hover:scale-110"
                                onClick={() => updateCount(adults, setAdults, -1)}
                            >
                                <MinusIcon className="w-3 h-3" />
                            </button>
                            <span className="font-bold">{adults}</span>
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-success-green hover:scale-110"
                                onClick={() => updateCount(adults, setAdults, 1)}
                            >
                                <PlusIcon className="w-3 h-3" />
                            </button>
                        </div>
                    </div>

                    {/* Seniors */}
                    <div className="flex items-center justify-between p-4 mb-4 bg-gray-100 border rounded-lg shadow-md">
                        <span className="font-bold text-gray-700">Seniors (70 ans et +)</span>
                        <div className="flex items-center space-x-2">
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-danger-red hover:scale-110"
                                onClick={() => updateCount(senior, setSenior, -1)}
                            >
                                <MinusIcon className="w-3 h-3" />
                            </button>
                            <span className="font-bold">{senior}</span>
                            <button
                                className="p-2 text-white transition transform rounded-full shadow-md bg-success-green hover:scale-110"
                                onClick={() => updateCount(senior, setSenior, 1)}
                            >
                                <PlusIcon className="w-3 h-3" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Forfait hospitalier */}
                    <div className="p-4 mb-4 bg-gray-100 border rounded-lg shadow-md">
                        <label className="flex mb-2 font-bold text-gray-700">Forfait hospitalier ({optionPrice} HT par personne éligible) :</label>
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                checked={isOptionActive}
                                onChange={(e) => setIsOptionActive(e.target.checked)}
                                className="mr-2 leading-tight accent-axa-blue-400"
                            />
                            <span className="text-gray-700">Activer l'option</span>
                        </div>
                    </div>

                    {/* --- Mode de tarification (Groupe / Famille) --- */}
                    <div className="p-4 mb-4 bg-gray-100 border rounded-lg shadow-md">
                        <label className="flex mb-2 font-bold text-gray-700">Tarification :</label>
                        <div className="flex items-center justify-between">
                            {/* Libellés du switch */}
                            <span className={`font-medium ${!isFamily ? 'text-axa-blue-400' : 'text-gray-500'}`}>
                                Groupe
                            </span>

                            {/* Switch stylisé */}
                            <label className="relative inline-flex items-center mx-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={isFamily}
                                    onChange={(e) => setIsFamily(e.target.checked)}
                                    className="sr-only peer"
                                    aria-label="Activer le tarif Famille"
                                />
                                {/* Track */}
                                <div className="transition-colors duration-300 bg-gray-200 border border-black rounded-full w-14 h-7 peer-focus:outline-none peer-checked:bg-axa-blue-400 peer-checked:border-none"></div>
                                {/* Thumb */}
                                <div className="absolute w-5 h-5 transition-transform duration-300 rounded-full shadow left-1 top-1 bg-axa-blue-400 peer-checked:bg-white peer-checked:translate-x-7"></div>
                            </label>

                            <span className={`font-medium ${isFamily ? 'text-axa-blue-400' : 'text-gray-500'}`}>
                                Solo/Duo/Famille
                            </span>
                        </div>
                    </div>

                </div>

                <div className='flex justify-center w-full space-x-4'>
                    <button
                        onClick={downloadDevis}
                        className="w-1/2 px-4 py-2 font-bold text-white transition duration-300 rounded bg-sienna-400 hover:bg-orange-600 hover:bg-axa-blue-700"
                    >
                        Télécharger le devis
                    </button>
                </div>

                {/* Résultats du calcul */}
                {resultHT && (
                    <div className="p-6 mt-4 border border-l-4 rounded-lg shadow-md bg-gray-50 border-warning-yellow">
                        <div className="flex items-center mb-4">
                            <div className="p-3 mr-4 rounded-full bg-warning-yellow">
                                <FaMoneyBillWave className="w-6 h-6 text-white" />
                            </div>
                            <h3 className="text-xl font-bold text-warning-yellow">Tarif</h3>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="p-4 rounded-lg shadow-sm bg-yellow-50">
                                <p className="text-gray-800"><strong>Montant HT : </strong>{resultHT} XFP</p>
                            </div>
                            <div className="p-4 rounded-lg shadow-sm bg-yellow-50">
                                <p className="text-gray-800"><strong>Montant TTC : </strong>{resultTTC} XFP</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default EstimerContrat;
